import React, { useState, useEffect } from 'react';
import './BillingRuleEditor.css';
import { getAccessToken } from '../utils/auth';
import { saveBillingRule } from '../utils/httpApi';

/**
 * Admin form to create or edit a single billing_rule. Pass an empty/default
 * rule (with id=0) to create; pass an existing rule to edit. On save the
 * backend appends a row to billing_rule_audit (before/after JSON), keeping
 * the change history visible for spec §3.2 compliance.
 */

const CATEGORY_OPTIONS = [
  'LANDING', 'TAKEOFF', 'PARKING', 'PASSENGER',
  'NOISE', 'EMISSIONS', 'SECURITY', 'PRM', 'OTHER',
];

const BASIS_OPTIONS = [
  'PER_TONNE_MTOW',
  'PER_TONNE_MTOW_BRACKETED',
  'PER_PAX',
  'PER_PAX_BRACKETED',
  'PER_MOVEMENT',
  'PER_QUARTER_HOUR',
  'PER_FIFTEEN_MIN_BLOCK',
  'PER_PARKING_MINUTE_BAND',
  'PERCENTAGE_OF',
  'FIXED',
];

function apiOk(resp) {
  const s = resp && resp.header && resp.header.apiStatus;
  return s === 'OK' || s === 'SUCCESS';
}

function BillingRuleEditor({ airportIcao, rule, onClose, onSaved }) {
  const [draft, setDraft] = useState(rule);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft(rule); }, [rule]);

  // Esc closes the modal.
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function set(field, value) {
    setDraft((d) => ({ ...d, [field]: value }));
  }

  function numOrNull(s) {
    if (s === '' || s === null || s === undefined) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  function intOrNull(s) {
    if (s === '' || s === null || s === undefined) return null;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  }

  function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const token = getAccessToken();
    if (!token) { setError('Not authenticated.'); setSaving(false); return; }
    // Normalise numeric fields — empty strings become null
    const payload = {
      airportIcao,
      rule: {
        ...draft,
        rate: numOrNull(draft.rate) ?? 0,
        minCharge: numOrNull(draft.minCharge),
        maxCharge: numOrNull(draft.maxCharge),
        minChargeSeatsBelow: intOrNull(draft.minChargeSeatsBelow),
        freePeriodMinutes: intOrNull(draft.freePeriodMinutes),
        chargeUnitMinutes: intOrNull(draft.chargeUnitMinutes),
        priority: intOrNull(draft.priority) ?? 50,
        effectiveTo: draft.effectiveTo || null,
      },
    };
    saveBillingRule(payload, token)
      .then((resp) => {
        if (!apiOk(resp)) {
          const errs = resp && resp.header && resp.header.errors;
          setError(errs && errs[0] ? errs[0].description : 'Save failed.');
          return;
        }
        onSaved && onSaved(resp.rule, resp.created);
      })
      .catch((e2) => setError('Save error: ' + e2.message))
      .finally(() => setSaving(false));
  }

  const isCreate = !draft || !draft.id;

  return (
    <div className="bre-backdrop" onClick={onClose}>
      <div className="bre-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="bre-head">
            <h3 className="bre-title">
              {isCreate ? 'New billing rule' : 'Edit rule ' + draft.ruleCode}
            </h3>
            <button type="button" className="bre-close"
              onClick={onClose} aria-label="Close">×</button>
          </div>

          <div className="bre-body">
            <div className="bre-row">
              <label className="bre-field">
                <span>Rule code *</span>
                <input required value={draft.ruleCode || ''}
                  onChange={(e) => set('ruleCode', e.target.value.toUpperCase())} />
              </label>
              <label className="bre-field">
                <span>Category *</span>
                <select required value={draft.category || ''}
                  onChange={(e) => set('category', e.target.value)}>
                  <option value="">—</option>
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="bre-field bre-priority">
                <span>Priority</span>
                <input type="number" value={draft.priority ?? 50}
                  onChange={(e) => set('priority', e.target.value)} />
              </label>
            </div>

            <label className="bre-field bre-wide">
              <span>Description</span>
              <input value={draft.description || ''}
                onChange={(e) => set('description', e.target.value)} />
            </label>

            <div className="bre-row">
              <label className="bre-field">
                <span>Basis *</span>
                <select required value={draft.calculationBasis || ''}
                  onChange={(e) => set('calculationBasis', e.target.value)}>
                  <option value="">—</option>
                  {BASIS_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <label className="bre-field">
                <span>Rate *</span>
                <input type="number" step="0.0001" required value={draft.rate ?? ''}
                  onChange={(e) => set('rate', e.target.value)} />
              </label>
              <label className="bre-field">
                <span>Currency</span>
                <input maxLength={3} value={draft.currency || 'EUR'}
                  onChange={(e) => set('currency', e.target.value.toUpperCase())} />
              </label>
            </div>

            <div className="bre-row">
              <label className="bre-field">
                <span>Effective from *</span>
                <input type="date" required value={draft.effectiveFrom || ''}
                  onChange={(e) => set('effectiveFrom', e.target.value)} />
              </label>
              <label className="bre-field">
                <span>Effective to</span>
                <input type="date" value={draft.effectiveTo || ''}
                  onChange={(e) => set('effectiveTo', e.target.value)}
                  placeholder="open-ended" />
              </label>
            </div>

            <div className="bre-row">
              <label className="bre-field">
                <span>Min charge</span>
                <input type="number" step="0.01" value={draft.minCharge ?? ''}
                  onChange={(e) => set('minCharge', e.target.value)} />
              </label>
              <label className="bre-field">
                <span>Max charge</span>
                <input type="number" step="0.01" value={draft.maxCharge ?? ''}
                  onChange={(e) => set('maxCharge', e.target.value)} />
              </label>
              <label className="bre-field">
                <span>Min if seats &lt;</span>
                <input type="number" value={draft.minChargeSeatsBelow ?? ''}
                  onChange={(e) => set('minChargeSeatsBelow', e.target.value)} />
              </label>
            </div>

            <div className="bre-row">
              <label className="bre-field">
                <span>Free period (min)</span>
                <input type="number" value={draft.freePeriodMinutes ?? ''}
                  onChange={(e) => set('freePeriodMinutes', e.target.value)} />
              </label>
              <label className="bre-field">
                <span>Charge unit (min)</span>
                <input type="number" value={draft.chargeUnitMinutes ?? ''}
                  onChange={(e) => set('chargeUnitMinutes', e.target.value)} />
              </label>
              <label className="bre-field">
                <span>Applies-to rule code</span>
                <input value={draft.appliesToRuleCode || ''}
                  onChange={(e) => set('appliesToRuleCode', e.target.value.toUpperCase())} />
              </label>
            </div>

            <label className="bre-field bre-wide">
              <span>Notes</span>
              <textarea rows={2} value={draft.notes || ''}
                onChange={(e) => set('notes', e.target.value)} />
            </label>

            <label className="bre-field bre-wide">
              <span>Source ref</span>
              <input value={draft.sourceRef || ''}
                onChange={(e) => set('sourceRef', e.target.value)} />
            </label>

            {error && <div className="bre-error">{error}</div>}
          </div>

          <div className="bre-foot">
            <button type="button" className="bre-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="bre-btn bre-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (isCreate ? 'Create rule' : 'Save changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BillingRuleEditor;
